/**
 * messageTurns — pure grouping math for the transcript's vertical rhythm
 * (2026-08 reading-comfort round).
 *
 * Owner report: "messages are crowding each other". The transcript used ONE
 * uniform gap between every row, so a four-message answer from Prest0n and a
 * hand-off between speakers looked identical — the thread read as a wall
 * rather than as a conversation with turns.
 *
 * The fix is a speaker-aware rhythm: consecutive rows from the SAME sender sit
 * close together (they are one utterance continued), and a change of speaker
 * opens a visibly larger gap. This module owns only the question "does this
 * row start a new turn?" — the spacing values themselves live in
 * styles.ts (`TranscriptContent`), keyed off the `data-turn-start` attribute
 * the render stamps from `startsNewTurn`.
 *
 * Pure and DOM-free on purpose (same shape as threadNav.ts): the rhythm is
 * unit-testable without rendering, and the render loop pays only a string
 * compare per message — no state, no effects, nothing that could feed the
 * update-loop class of bug PROD-185 was about.
 */

/** The fields turn-grouping reads. Structural subset of ChatMessage. */
export interface TurnGroupableMessage {
  isUser?: boolean;
  senderId?: string | null;
  sourceTag?: string;
}

/**
 * Stable identity for "who is speaking", collapsing the several ways the
 * transcript expresses a sender:
 *
 * - `system`      — interstitials (low-balance banner, trial ended, queue
 *                   notices). Never merged into a neighbour's run: a system
 *                   notice interrupting a conversation must read as its own
 *                   beat.
 * - `self`        — the viewer. Solo threads and legacy bubbles carry no
 *                   senderId at all, so `isUser` alone resolves them; in a
 *                   group thread the viewer's own uid resolves here too, so a
 *                   run of the viewer's messages never splits just because
 *                   some rows predate senderId stamping.
 * - `participant:<uid>` — another human in a group thread. Two different
 *                   people always break the run, even though both render on
 *                   the left.
 * - `assistant`   — Prest0n.
 */
export function messageTurnKey(
  message: TurnGroupableMessage | null | undefined,
  currentUid?: string | null,
): string {
  if (!message) return 'none';
  const sourceTag = message.sourceTag || '';
  if (sourceTag.startsWith('system:')) return 'system';
  const senderId = message.senderId || null;
  if (message.isUser) return 'self';
  if (senderId) {
    return currentUid && senderId === currentUid ? 'self' : `participant:${senderId}`;
  }
  return 'assistant';
}

/**
 * True when `current` opens a new turn — i.e. it is the first row in the
 * transcript, or the speaker changed since `previous`. The render stamps
 * `data-turn-start` from this, and CSS turns that into the wider gap.
 */
export function startsNewTurn(
  previous: TurnGroupableMessage | null | undefined,
  current: TurnGroupableMessage | null | undefined,
  currentUid?: string | null,
): boolean {
  if (!previous) return true;
  return messageTurnKey(previous, currentUid) !== messageTurnKey(current, currentUid);
}
